import React,{useEffect,useState} from 'react';
import {useParams,useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {legalVaultService} from '../services/legalVaultService';
import RichTextEditor from '../components/RichTextEditor';
import AuditTrail from '../components/AuditTrail';
import SmartDatePicker from '../components/SmartDatePicker';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import mammoth from 'mammoth';
import TurndownService from 'turndown';
const {FiSave,FiEye,FiSend,FiCalendar,FiArrowLeft,FiChevronDown,FiChevronUp,FiAlertCircle,FiClock,FiUser,FiUpload,FiCheckCircle}=FiIcons;

function slugify(value='') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g,'-')
    .replace(/[^a-z0-9-]/g,'')
    .replace(/-+/g,'-')
    .replace(/^-|-$/g,'');
}

function formatDisplayDate(value) {
  if (!value) return 'Not saved yet';
  const parsed=new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not saved yet' : parsed.toLocaleString();
}

function normalizeImportedMarkdown(value='') {
  return String(value)
    .replace(/\u0000/g,'')
    .replace(/\u00A0/g,' ')
    .replace(/\r\n/g,'\n')
    .replace(/\r/g,'\n')
    .replace(/^(#{1,6}\s+)((?:\d+(?:\.\d+)*))\\\.(?=\s)/gm,'$1$2.')
    .replace(/\n{3,}/g,'\n\n');
}

export default function DocumentEditor() {
  const {documentId,versionId}=useParams();
  const navigate=useNavigate();
  const [document,setDocument]=useState(null);
  const [version,setVersion]=useState(null);
  const [brands,setBrands]=useState([]);
  const [isSaving,setIsSaving]=useState(false);
  const [showAdvanced,setShowAdvanced]=useState(false);
  const [slugManuallyEdited,setSlugManuallyEdited]=useState(false);
  const [alertMessage,setAlertMessage]=useState(null);
  const [showPublishDialog,setShowPublishDialog]=useState(false);
  const [publishData,setPublishData]=useState({effectiveDate: '',changeSummary: '',visibility: 'Private'});
  const [loadError,setLoadError]=useState(null);
  const [titleError,setTitleError]=useState('');
  const [typeError,setTypeError]=useState('');
  const [publishedVisibility,setPublishedVisibility]=useState('Private');
  const [isUpdatingVisibility,setIsUpdatingVisibility]=useState(false);
  const [importStatus,setImportStatus]=useState(null);
  const [importFilename,setImportFilename]=useState(null);
  const [pendingImportMarkdown,setPendingImportMarkdown]=useState(null);
  const [pendingImportFilename,setPendingImportFilename]=useState(null);

  const isNewDocument=documentId==='new';
  const canEditDocuments=legalVaultService.hasAdminAccess();
  const canManagePublishedVisibility=legalVaultService.hasSettingsAccess();

  useEffect(()=> {
    const loadData=async ()=> {
      if (isNewDocument) {
        setDocument({title: '',type: '',slug: '',tags: '',brandId: '',visibility: 'Private',reviewDate: ''});
        setVersion({versionNumber: 'v1.0',status: 'Draft',content: '# New Document\n\nStart writing...'});
        legalVaultService.getBrandingProfiles().then(setBrands);
      } else {
        try {
          const [doc,ver,profiles]=await Promise.all([
            legalVaultService.getDocumentById(documentId),
            versionId ? legalVaultService.getVersionById(versionId) : legalVaultService.getCurrentVersion(documentId),
            legalVaultService.getBrandingProfiles(),
          ]);
          if (!doc) { throw new Error('Document could not be loaded.'); }
          if (!ver) { throw new Error('A saved version could not be loaded for this document.'); }
          setDocument(doc);
          setVersion(ver);
          setBrands(profiles);
          setPublishedVisibility(doc.visibility || 'Private');
          setLoadError(null);
        } catch (error) {
          setLoadError(error.message);
        }
      }
    };
    loadData();
  },[documentId,versionId]);

  const handleTitleChange=(newTitle)=> {
    const updates={title: newTitle};
    if (isNewDocument && !slugManuallyEdited) { updates.slug=slugify(newTitle); }
    setDocument({...document,...updates});
    setTitleError('');
  };

  const handleSlugChange=(newSlug)=> {
    setDocument({...document,slug: newSlug});
    if (isNewDocument) setSlugManuallyEdited(true);
  };

  const handleSave=async ()=> {
    if (isSaving) return;
    setTitleError('');
    setTypeError('');
    if (isNewDocument) {
      if (!document.title?.trim()) {
        setTitleError("Document title is required.");
        setAlertMessage("Document title is required.");
        return;
      }
      if (!document.type) {
        setTypeError("Document type is required.");
        setAlertMessage("Document type is required.");
        return;
      }
      const finalSlug=document.slug?.trim() || slugify(document.title);
      if (!finalSlug) {
        setAlertMessage("Unable to generate a URL slug. Please enter a document title.");
        return;
      }
    }
    setIsSaving(true);
    try {
      if (isNewDocument) {
        const documentToSave={...document,slug: document.slug?.trim() || slugify(document.title)};
        const newDoc=await legalVaultService.createDocument(documentToSave);
        if (!newDoc?.id) throw new Error("Document record was not created correctly.");
        let newVersion;
        try {
          newVersion=await legalVaultService.createVersion(newDoc.id,{...version,status: 'Draft',changeSummary: 'Initial draft'});
        } catch (versionError) {
          let cleanup;
          try {
            cleanup=await legalVaultService.cleanupFailedNewDocument(newDoc.id);
          } catch (cleanupError) {
            throw new Error(`${versionError.message} An incomplete draft may remain. Document ID: ${newDoc.id}. Cleanup error: ${cleanupError.message}`);
          }
          if (cleanup?.deleted) {
            throw new Error(`${versionError.message} The incomplete document record was removed safely.`);
          }
          throw new Error(`${versionError.message} An incomplete draft may remain. Document ID: ${newDoc.id}. ${cleanup?.reason || ''}`.trim());
        }
        await legalVaultService.verifyDocumentPersistence(newDoc.id,newVersion.id);
        setDocument(newDoc);
        setVersion(newVersion);
        navigate(`/documents/${newDoc.id}/versions/${newVersion.id}/edit`);
      } else {
        const activeVersionId=versionId || version?.id;
        await legalVaultService.updateDocument(documentId,document);
        await legalVaultService.updateVersion(activeVersionId,{content: version.content});
        await legalVaultService.verifyDocumentPersistence(documentId,activeVersionId);
        setAlertMessage("Draft saved successfully.");
      }
    } catch (error) {
      setAlertMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish=async ()=> {
    if (!version?.id) { setAlertMessage('No version available to publish.'); return; }
    if (!publishData.effectiveDate) { setAlertMessage('Effective date is required for publication.'); return; }
    try {
      await legalVaultService.publishVersion(version.id,publishData);
      setShowPublishDialog(false);
      navigate('/documents');
    } catch (error) {
      setAlertMessage(error.message);
    }
  };

  const handleUpdatePublishedVisibility=async ()=> {
    if (!canManagePublishedVisibility) {
      setAlertMessage('Only owners and admins can update published visibility.');
      return;
    }
    setIsUpdatingVisibility(true);
    try {
      const updatedDoc=await legalVaultService.updatePublishedVisibility(document.id,publishedVisibility);
      setDocument(updatedDoc);
      setAlertMessage('Published visibility updated successfully.');
    } catch (error) {
      setAlertMessage(error.message);
    } finally {
      setIsUpdatingVisibility(false);
    }
  };

  const handleImportDocx=async (e)=> {
    const file=e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setAlertMessage('Only .docx files can be imported. Please select a Word document.');
      e.target.value='';
      return;
    }
    setImportStatus('importing');
    setImportFilename(file.name);
    try {
      const arrayBuffer=await file.arrayBuffer();
      const mammothResult=await mammoth.convertToHtml(
        {arrayBuffer},
        {styleMap:["p[style-name='Title'] => h1:fresh"]}
      );
      if (mammothResult.messages && mammothResult.messages.length > 0) {
        const errors=mammothResult.messages.filter(m=> m.type==='error');
        if (errors.length > 0) {
          setAlertMessage('The Word document could not be read. It may be corrupt or password-protected.');
          setImportStatus(null);
          setImportFilename(null);
          e.target.value='';
          return;
        }
      }
      const html=mammothResult.value;
      if (!html || html.trim().length===0) {
        setAlertMessage('The Word document appears to be empty or unsupported.');
        setImportStatus(null);
        setImportFilename(null);
        e.target.value='';
        return;
      }
      const turndownService=new TurndownService({
        headingStyle: 'atx',
        bulletListMarker: '-',
        codeBlockStyle: 'fenced',
      });
      turndownService.addRule('table',{
        filter: 'table',
        replacement: function(content,node) {
          const rows=[];
          const tbody=node.querySelector('tbody') || node;
          const trs=tbody.querySelectorAll('tr');
          for (const tr of trs) {
            const cells=[];
            const tds=tr.querySelectorAll('td,th');
            for (const td of tds) { cells.push(td.textContent.trim()); }
            if (cells.length > 0) { rows.push(cells.join(' | ')); }
          }
          return rows.length > 0 ? rows.join('\n') + '\n' : '';
        }
      });
      turndownService.addRule('removeImages',{
        filter: 'img',
        replacement: function() { return ''; }
      });
      turndownService.addRule('removeScripts',{
        filter: ['script','style','noscript'],
        replacement: function() { return ''; }
      });
      const markdown=normalizeImportedMarkdown(turndownService.turndown(html));
      const defaultContent='# New Document\n\nStart writing...';
      const isDefaultContent=version.content===defaultContent || version.content.trim()===defaultContent.trim();
      if (!isDefaultContent && version.content.trim().length > 0) {
        setImportStatus('confirming');
        setPendingImportMarkdown(markdown);
        setPendingImportFilename(file.name);
        e.target.value='';
        return;
      }
      applyImportedContent(markdown,file.name);
    } catch (error) {
      console.error('DOCX import error:',error);
      if (error.message?.includes('password') || error.message?.includes('encrypted')) {
        setAlertMessage('This Word document is password-protected and cannot be imported.');
      } else {
        setAlertMessage('The Word document could not be imported. It may be corrupt, unsupported, or password-protected.');
      }
      setImportStatus(null);
      setImportFilename(null);
      e.target.value='';
    }
  };

  const applyImportedContent=(markdown,filename)=> {
    setVersion({...version,content: markdown});
    setImportStatus('complete');
    setImportFilename(filename);
    setPendingImportMarkdown(null);
    setPendingImportFilename(null);
    if (!document.title?.trim()) {
      const titleFromFilename=filename
        .replace(/\.docx$/i,'')
        .replace(/\s*\(\d+\)$/, '')
        .replace(/[_-]+/g,' ')
        .replace(/\s+/g,' ')
        .trim();
      const h1Match=markdown.match(/^#\s+(.+)$/m);
      const fallbackHeading=h1Match?.[1]?.trim() || '';
      const importedTitle=titleFromFilename || fallbackHeading;
      if (importedTitle) {
        setDocument({...document,title: importedTitle,slug: slugify(importedTitle)});
        setTitleError('');
      }
    }
    if (!document.type) {
      setTypeError('Document type is required before saving.');
    }
  };

  const confirmImportReplace=()=> {
    if (pendingImportMarkdown) {
      applyImportedContent(pendingImportMarkdown,pendingImportFilename);
    }
  };

  const cancelImportReplace=()=> {
    setImportStatus(null);
    setImportFilename(null);
    setPendingImportMarkdown(null);
    setPendingImportFilename(null);
  };

  const openPublishModal=()=> {
    setPublishData({effectiveDate: '',changeSummary: '',visibility: document.visibility || 'Private'});
    setShowPublishDialog(true);
  };

  if (loadError) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <SafeIcon icon={FiAlertCircle} className="text-4xl text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">Loading Error</h3>
          <p className="text-red-700 mb-6">{loadError}</p>
          <button onClick={()=> navigate('/documents')} className="px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors">
            Back to Documents
          </button>
        </div>
      </div>
    );
  }
  if (!document || !version) return <div className="p-8 text-center text-slate-500">Loading...</div>;

  const isReadOnly=!canEditDocuments || ['Published','Archived','Superseded','Withdrawn','Scheduled'].includes(version.status);

  const renderAdvancedSettingsFields=(compact=false)=> (
    <div className={compact ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 gap-6'}>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">URL Slug</label>
        <input type="text" value={document.slug || ''} onChange={(e)=> handleSlugChange(e.target.value)} disabled={isReadOnly} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" placeholder="my-document-url" />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Visibility</label>
        <select value={document.visibility} onChange={(e)=> setDocument({...document,visibility: e.target.value})} disabled={isReadOnly} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
          <option value="Private">Private</option>
          <option value="Public">Public</option>
          <option value="Unlisted">Unlisted</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Assigned Brand</label>
        <select value={document.brandId} onChange={(e)=> setDocument({...document,brandId: e.target.value})} disabled={isReadOnly} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
          <option value="">Default Brand</option>
          {brands.map(b=> <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Review Date</label>
        <SmartDatePicker
          value={document.reviewDate || ''}
          onChange={(value)=> setDocument({...document,reviewDate:value})}
          disabled={isReadOnly}
          placeholder="Set review date"
          ariaLabel="Document review date"
          presets={[
            {label:'+30 days',daysFromToday:30},
            {label:'+90 days',daysFromToday:90},
            {label:'+1 year',daysFromToday:365},
          ]}
        />
      </div>
      {document.status==='Published' && canManagePublishedVisibility && (
        <div className={`${compact ? '' : 'md:col-span-2'} border-t border-slate-200 pt-5 mt-1`}>
          <h4 className="text-md font-semibold text-slate-800 mb-2">Published Visibility</h4>
          <p className="text-sm text-slate-500 mb-4">Published content is locked. Visibility can still be changed without modifying the published version.</p>
          <div className={compact ? 'space-y-3' : 'flex items-center gap-4'}>
            <select value={publishedVisibility} onChange={(e)=> setPublishedVisibility(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
              <option value="Private">Private</option>
              <option value="Public">Public</option>
              <option value="Unlisted">Unlisted</option>
              <option value="Password Protected">Password Protected</option>
            </select>
            <button onClick={handleUpdatePublishedVisibility} disabled={isUpdatingVisibility} className={`${compact ? 'w-full' : ''} px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap`}>
              {isUpdatingVisibility ? 'Updating...' : 'Update Visibility'}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="space-y-6 max-w-6xl mx-auto pb-12">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={()=> navigate('/documents')} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <SafeIcon icon={FiArrowLeft} className="text-xl" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{isNewDocument ? 'Create New Document' : `Edit: ${document.title}`}</h2>
            <p className="text-slate-500 mt-1">{version.status} Version {version.versionNumber}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {!isReadOnly && (
            <>
              <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50">
                <SafeIcon icon={FiSave} className="mr-2" />
                {isSaving ? 'Saving...' : 'Save Draft'}
              </button>
              {!isNewDocument && version.status==='Draft' && (
                <button onClick={openPublishModal} className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700">
                  <SafeIcon icon={FiSend} className="mr-2" />
                  Publish
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
        <div className="xl:col-span-8 2xl:col-span-9 space-y-6 min-w-0">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Document Title *</label>
                <input type="text" value={document.title} onChange={(e)=> handleTitleChange(e.target.value)} disabled={isReadOnly} className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none ${titleError ? 'border-red-400' : 'border-slate-300'}`} placeholder="e.g. Terms of Service" />
                {titleError && <p className="mt-1 text-xs text-red-600">{titleError}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Document Type *</label>
                <select value={document.type} onChange={(e)=> {setTypeError('');setDocument({...document,type: e.target.value});}} disabled={isReadOnly} className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none ${typeError ? 'border-red-400' : 'border-slate-300'}`}>
                  <option value="">Select type...</option>
                  <option value="Service Terms & Conditions">Service Terms & Conditions</option>
                  <option value="Website Design Terms">Website Design Terms</option>
                  <option value="Software Application Terms">Software Application Terms</option>
                  <option value="SaaS Terms">SaaS Terms</option>
                  <option value="Support & Maintenance Policies">Support & Maintenance Policies</option>
                  <option value="Licence Terms">Licence Terms</option>
                  <option value="Privacy Policies">Privacy Policies</option>
                  <option value="Data Sharing Policies">Data Sharing Policies</option>
                  <option value="Refund and Cancellation Policies">Refund and Cancellation Policies</option>
                  <option value="Disclaimers">Disclaimers</option>
                  <option value="Custom">Custom</option>
                </select>
                {typeError && <p className="mt-1 text-xs text-red-600">{typeError}</p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Content Editor</h3>
              {!isReadOnly && (
                <label className="cursor-pointer flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm">
                  <SafeIcon icon={FiUpload} className="mr-2" />
                  Import Word Document
                  <input type="file" accept=".docx" onChange={handleImportDocx} className="hidden" disabled={importStatus==='importing'} />
                </label>
              )}
            </div>
            {importStatus==='importing' && <div className="mb-3 flex items-center text-sm text-blue-600"><div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>Importing {importFilename}...</div>}
            {importStatus==='complete' && <div className="mb-3 flex items-center text-sm text-emerald-600 font-medium"><SafeIcon icon={FiCheckCircle} className="mr-2" />Imported from {importFilename}</div>}
            {!isReadOnly && <p className="text-xs text-slate-500 mb-3">Paste as plain text, or import a Word document to preserve headings, lists and basic formatting. Word-generated escaped periods in numbered headings are normalised automatically.</p>}
            <RichTextEditor content={version.content} onChange={(v)=> setVersion({...version,content: v})} readOnly={isReadOnly} />
          </div>

          <div className="xl:hidden bg-white rounded-xl border border-slate-200 shadow-sm overflow-visible">
            <button onClick={()=> setShowAdvanced(!showAdvanced)} aria-expanded={showAdvanced} className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center"><SafeIcon icon={showAdvanced ? FiChevronUp : FiChevronDown} className="mr-2" />Advanced Settings</h3>
            </button>
            {showAdvanced && <div className="p-6 border-t border-slate-100">{renderAdvancedSettingsFields(false)}</div>}
          </div>
        </div>

        <aside className="xl:col-span-4 2xl:col-span-3 space-y-6 min-w-0">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Version Metadata</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center gap-3 text-sm"><span className="text-slate-500 flex items-center"><SafeIcon icon={FiClock} className="mr-2" /> Created</span><span className="text-slate-900 font-medium text-right">{formatDisplayDate(version.createdDate || version.created)}</span></div>
              <div className="flex justify-between items-center gap-3 text-sm"><span className="text-slate-500 flex items-center"><SafeIcon icon={FiClock} className="mr-2" /> Last Updated</span><span className="text-slate-900 font-medium text-right">{formatDisplayDate(version.updatedDate || version.updated)}</span></div>
              <div className="flex justify-between items-center gap-3 text-sm"><span className="text-slate-500 flex items-center"><SafeIcon icon={FiUser} className="mr-2" /> Author</span><span className="text-slate-900 font-medium text-right">BPA Admin</span></div>
            </div>
          </div>

          <div className="hidden xl:block bg-white rounded-xl border border-slate-200 p-6 shadow-sm overflow-visible">
            <div className="mb-5"><h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Advanced Settings</h3><p className="text-xs text-slate-500 mt-1">Document access, branding and review controls.</p></div>
            {renderAdvancedSettingsFields(true)}
          </div>

          {!isNewDocument && canEditDocuments && <AuditTrail documentId={documentId} />}
        </aside>
      </div>

      {showPublishDialog && <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"><div className="bg-white rounded-xl p-6 shadow-xl w-full max-w-md overflow-visible"><h3 className="text-lg font-semibold mb-4 text-slate-900">Publish Version</h3><div className="space-y-4"><div><label className="block text-sm font-medium text-slate-700 mb-1">Effective Date</label><SmartDatePicker value={publishData.effectiveDate} onChange={(value)=> setPublishData({...publishData,effectiveDate:value})} placeholder="Select effective date" allowClear={false} ariaLabel="Publication effective date" presets={[{label:'Tomorrow',daysFromToday:1},{label:'+7 days',daysFromToday:7},{label:'+30 days',daysFromToday:30}]} /></div><div><label className="block text-sm font-medium text-slate-700 mb-1">Change Summary</label><textarea value={publishData.changeSummary} onChange={(e)=> setPublishData({...publishData,changeSummary: e.target.value})} rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none" placeholder="What changed in this version?" /></div><div><label className="block text-sm font-medium text-slate-700 mb-1">Publication Visibility</label><select value={publishData.visibility} onChange={(e)=> setPublishData({...publishData,visibility: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"><option value="Private">Private</option><option value="Public">Public</option><option value="Unlisted">Unlisted</option></select><p className="text-xs text-slate-500 mt-1">Public appears in the Contract & Policy Hub. Unlisted is accessible only through its direct link. Private remains internal.</p></div></div><div className="flex justify-end space-x-3 mt-6"><button onClick={()=> setShowPublishDialog(false)} className="px-4 py-2 text-slate-600 font-medium">Cancel</button><button onClick={handlePublish} className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors">Confirm & Publish</button></div></div></div>}

      {importStatus==='confirming' && pendingImportMarkdown && <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"><div className="bg-white rounded-xl p-6 shadow-xl w-full max-w-md"><h3 className="text-lg font-semibold mb-4 text-slate-900">Replace Existing Content?</h3><p className="text-slate-600 mb-2">The editor already contains content. Importing from <strong>{pendingImportFilename}</strong> will replace it.</p><p className="text-sm text-slate-500 mb-6">Your current draft will be lost. This action cannot be undone.</p><div className="flex justify-end space-x-3"><button onClick={cancelImportReplace} className="px-4 py-2 text-slate-600 font-medium hover:text-slate-800 transition-colors">Cancel</button><button onClick={confirmImportReplace} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">Replace with Import</button></div></div></div>}

      {alertMessage && <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"><div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md"><div className="p-6"><h3 className="text-lg font-semibold text-slate-900 flex items-center"><SafeIcon icon={FiAlertCircle} className="mr-2 text-blue-600" />System Message</h3><p className="text-slate-600 mt-2">{alertMessage}</p></div><div className="flex justify-end p-4 border-t border-slate-100"><button onClick={()=> setAlertMessage(null)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">OK</button></div></div></div>}
    </motion.div>
  );
}